import { api } from './client'
import { requireArrayIn } from './readShape'

const OTHERS_SHAPE = {
  noun: 'list of other users’ saved views',
  emptyNoun: 'list',
  entryNoun: 'view',
}

/**
 * Saved report views — cb-api's `/report-views` domain.
 *
 * As of CC-50282 Phase 2 this covers ALL THREE scopes, not just the shared two:
 * a view saved for "Only me" is stored server-side like any other, so it follows
 * its author between devices and can be promoted to a group or the firm without
 * being rebuilt. Personal views used to live in `preferencesStore` (localStorage)
 * and were therefore per-device, unpromotable, and destroyed by clearing site
 * data. `importLocal` exists to adopt the ones already on people's machines.
 *
 * Every view carries the same v2 payload (filters + layout + pageState),
 * whatever its scope.
 */
const reportViewsService = {
  /**
   * Saved views the current user may see for this report.
   *
   * Returns the ENVELOPE, not just the array: `storageConfigured` tells the menu
   * whether this environment has the D1 binding at all, and `scopesResolved`
   * false means a group-membership lookup broke so group views may be missing.
   * An empty list that silently means "the lookup failed" is exactly how
   * CC-49595 reached QA looking functional.
   *
   * `{ includeOthers: true }` (spec 173) asks for OTHER users' views instead —
   * the ones the plain list does not return — which cb-api answers only for CB's
   * SysAdmin account (403 for anyone else, 503 when CB cannot confirm, 502 when
   * storage cannot be read; every one of those REJECTS here). It resolves to
   * `{ others, storageConfigured }` and deliberately NOT to `views`, so the
   * caller cannot fold someone else's view into its own lists by accident. Each
   * entry must carry `adminView: true`; anything else is a reply we could not
   * read, and throws rather than rendering as "nobody else has one".
   */
  async list(reportKey, { includeOthers = false } = {}) {
    if (includeOthers) {
      const data = await api.get(`/report-views/${encodeURIComponent(reportKey)}`, { include: 'others' })
      const others = requireArrayIn(data, 'others', OTHERS_SHAPE)
      if (!others.every((v) => v && typeof v === 'object' && v.adminView === true && typeof v.name === 'string')) {
        throw new Error(
          `The ${OTHERS_SHAPE.noun} came back with a malformed ${OTHERS_SHAPE.entryNoun}. ` +
          `This is not an empty ${OTHERS_SHAPE.emptyNoun} — try again, and report it if it persists.`
        )
      }
      return { others, storageConfigured: data?.data?.storageConfigured !== false }
    }
    const data = await api.get(`/report-views/${encodeURIComponent(reportKey)}`)
    const d = data?.data || {}
    return {
      views: d.views || [],
      storageConfigured: d.storageConfigured !== false,
      scopesResolved: d.scopesResolved !== false,
    }
  },

  /** Create or overwrite a view. payload = { name, scope, groupId?, groupName?, view }. */
  async save(reportKey, payload) {
    const data = await api.put(`/report-views/${encodeURIComponent(reportKey)}`, payload)
    return data?.data
  },

  /**
   * Rename a view and/or move it between scopes, in place — same id, same
   * payload. patch = { name?, scope?, groupId?, groupName? }. Creator only.
   */
  async update(reportKey, id, patch) {
    const data = await api.put(`/report-views/${encodeURIComponent(reportKey)}/${encodeURIComponent(id)}`, patch)
    return data?.data
  },

  /** Delete a view (creator, or an admin for a shared one — server-enforced). */
  async remove(reportKey, id) {
    const data = await api.delete(`/report-views/${encodeURIComponent(reportKey)}/${encodeURIComponent(id)}`)
    return data?.data
  },

  /**
   * Adopt device-local views into the signed-in account, at "Only me" scope.
   * entries = [{ reportKey, name, view }].
   *
   * Idempotent server-side: a name that already exists at `mine` scope is
   * skipped, never overwritten — so a user with two machines can run this from
   * both without the older snapshot clobbering the newer one.
   */
  async importLocal(entries) {
    const data = await api.post('/report-views/import', { views: entries })
    return data?.data || { imported: 0, skipped: 0, rejected: 0 }
  },

  /**
   * Groups the signed-in user may share a view WITH — i.e. groups they belong to.
   *
   * Deliberately NOT /security/groups: that lists every group on the tenant, but
   * sharing is restricted to your own groups, so the picker was offering options
   * that could only ever fail (CC-49595 — 7 of 10 vladdemo groups have no active
   * members, and the ones the user was actually in weren't listed).
   */
  async groups() {
    const data = await api.get('/report-views/groups')
    return data?.data?.groups || []
  },
}

export default reportViewsService
